import { isValidUuid } from '../validation/zod-helpers/is-valid-uuid.js';
import { findRuleCycle } from './find-rule-cycle.js';
import { isActorCapableField } from './is-actor-capable-field.js';
import { isPerProductFieldId } from './is-per-product-field-id.js';
import { isServerSetField } from './is-server-set-field.js';
import { isStagedTagKey } from './is-staged-tag-key.js';
import { conditionErrorMessage } from './rule-condition-error.js';
import { ruleConditions } from './rule-conditions.js';
import { ruleDefaultConfigError } from './rule-default-config-error.js';
import { rulePhaseErrors } from './rule-phase-errors.js';
import { CORE_FIELD_ID_SET, CORE_FIELD_OPERATORS, DEFAULT_TO_ACTOR, DERIVED_CORE_FIELD_IDS, FIELD_TYPE_OPERATORS, RULE_TYPE_SET, SETTABLE_CORE_FIELD_IDS, } from './rule-types.js';
import { valueKindForField } from './value-kind-for-field.js';
import { valueMatchesKind } from './value-matches-kind.js';
const SETTABLE_CORE_FIELD_ID_SET = new Set(SETTABLE_CORE_FIELD_IDS);
const DERIVED_CORE_FIELD_ID_SET = new Set(DERIVED_CORE_FIELD_IDS);
function fieldOperators(fieldId, fields) {
    if (CORE_FIELD_ID_SET.has(fieldId)) {
        return CORE_FIELD_OPERATORS[fieldId];
    }
    const field = fields.find((candidate) => candidate.id === fieldId);
    return field ? FIELD_TYPE_OPERATORS[field.type] : undefined;
}
/** Author-time checks a template's rule graph must pass before it can be saved/published. */
export function validateRules(fields, rules) {
    const errors = [];
    const cycle = findRuleCycle(rules);
    if (cycle) {
        errors.push({
            ruleIndex: -1,
            message: `Circular rule dependency: ${cycle.join(' → ')}`,
        });
    }
    for (const field of fields) {
        if (CORE_FIELD_ID_SET.has(field.id)) {
            errors.push({
                ruleIndex: -1,
                message: `Field id "${field.id}" collides with a core field id — choose a different id.`,
            });
        }
    }
    const targetsByType = new Map();
    const defaultValueIfCountByTarget = new Map();
    rules.forEach((rule, ruleIndex) => {
        if (!RULE_TYPE_SET.has(rule.type)) {
            errors.push({ ruleIndex, message: `Unknown rule type "${String(rule.type)}".` });
            return;
        }
        for (const condition of ruleConditions(rule)) {
            const conditionError = conditionErrorMessage({ ...rule, condition }, fieldOperators(condition.fieldId, fields), fields);
            if (conditionError)
                errors.push({ ruleIndex, message: conditionError });
        }
        if (rule.type === 'addTagIf') {
            if ((!isValidUuid(rule.targetFieldId) ||
                rule.targetFieldId !== rule.targetFieldId.toLowerCase()) &&
                !isStagedTagKey(rule.targetFieldId)) {
                errors.push({ ruleIndex, message: 'addTagIf must name a tag.' });
            }
            for (const message of rulePhaseErrors(rule, fields))
                errors.push({ ruleIndex, message });
            return;
        }
        if (isPerProductFieldId(rule.targetFieldId, fields)) {
            errors.push({
                ruleIndex,
                message: `${rule.type} cannot target per-product field "${rule.targetFieldId}" — it has no single value to drive.`,
            });
            return;
        }
        if (!fieldOperators(rule.targetFieldId, fields)) {
            errors.push({
                ruleIndex,
                message: `Rule targets unknown field "${rule.targetFieldId}".`,
            });
        }
        for (const message of rulePhaseErrors(rule, fields))
            errors.push({ ruleIndex, message });
        if (rule.type !== 'visibleIf' && isServerSetField(rule.targetFieldId, fields)) {
            errors.push({
                ruleIndex,
                message: `${rule.type} cannot target SYSTEM_TIMESTAMP field "${rule.targetFieldId}" — it is server-set, not user-editable.`,
            });
            return;
        }
        if (rule.type === 'defaultValueIf') {
            if (rule.value === DEFAULT_TO_ACTOR) {
                if (valueKindForField(rule.targetFieldId, fields) &&
                    !isActorCapableField(rule.targetFieldId, fields)) {
                    errors.push({
                        ruleIndex,
                        message: `Rule value for "${rule.targetFieldId}" cannot default to the acting user — that field is not actor-capable.`,
                    });
                }
            }
            else {
                const defaultKind = valueKindForField(rule.targetFieldId, fields);
                const configError = ruleDefaultConfigError(rule, fields);
                if (defaultKind && !valueMatchesKind(rule.value, defaultKind)) {
                    errors.push({
                        ruleIndex,
                        message: `Rule value for "${rule.targetFieldId}" does not match its ${defaultKind} type.`,
                    });
                }
                else if (configError) {
                    errors.push({ ruleIndex, message: configError });
                }
            }
        }
        if (rule.type === 'visibleIf' && SETTABLE_CORE_FIELD_ID_SET.has(rule.targetFieldId)) {
            errors.push({
                ruleIndex,
                message: `visibleIf cannot target core gate "${rule.targetFieldId}" — core gates are always visible.`,
            });
        }
        if (DERIVED_CORE_FIELD_ID_SET.has(rule.targetFieldId)) {
            errors.push({
                ruleIndex,
                message: `${rule.type} cannot target derived core state "${rule.targetFieldId}" — it is read-only.`,
            });
        }
        if (rule.type === 'defaultValueIf') {
            defaultValueIfCountByTarget.set(rule.targetFieldId, (defaultValueIfCountByTarget.get(rule.targetFieldId) ?? 0) + 1);
        }
        const seenTypes = targetsByType.get(rule.targetFieldId) ?? new Set();
        seenTypes.add(rule.type);
        targetsByType.set(rule.targetFieldId, seenTypes);
    });
    for (const [targetFieldId, types] of targetsByType) {
        if (types.has('requiredIf') && types.has('visibleIf')) {
            errors.push({
                ruleIndex: -1,
                message: `Field "${targetFieldId}" cannot have both a requiredIf and a visibleIf rule.`,
            });
        }
        if (types.has('visibleIf') && types.has('defaultValueIf')) {
            errors.push({
                ruleIndex: -1,
                message: `Field "${targetFieldId}" cannot have both a visibleIf and a defaultValueIf rule — a default could be written into a field the user never sees.`,
            });
        }
    }
    for (const [targetFieldId, count] of defaultValueIfCountByTarget) {
        if (count > 1) {
            errors.push({
                ruleIndex: -1,
                message: `Field "${targetFieldId}" cannot have more than one defaultValueIf rule — the applied default would depend on rule evaluation order.`,
            });
        }
    }
    return { valid: errors.length === 0, errors };
}
//# sourceMappingURL=rule-validator.js.map